import { Goal, ID } from '@griply/types';

import { getArchivedGoalIds } from '../derived-goal-state';

export type GoalTreeNode = {
  goal: Goal;
  children: GoalTreeNode[];
};

export type BuildGoalTreeOptions = {
  hideArchived?: boolean;
  hideCompleted?: boolean;
};

export const buildGoalTree = (
  goals: Goal[],
  { hideArchived, hideCompleted }: BuildGoalTreeOptions = {},
): GoalTreeNode[] => {
  const archivedIds = getArchivedGoalIds(goals);
  const goalMap = new Map<ID, Goal>();
  const childrenMap = new Map<ID, Goal[]>();

  for (const goal of goals) {
    goalMap.set(goal.id, goal);
  }

  for (const goal of goals) {
    if (hideArchived && archivedIds.has(goal.id)) {
      continue;
    }
    if (hideCompleted && goal.completedAt) {
      continue;
    }

    const directParentId = goal.parentIds?.[goal.parentIds.length - 1];
    if (directParentId && goalMap.has(directParentId)) {
      if (!childrenMap.has(directParentId)) {
        childrenMap.set(directParentId, []);
      }
      childrenMap.get(directParentId)!.push(goal);
    }
  }

  const buildNode = (goal: Goal): GoalTreeNode => {
    const children = (childrenMap.get(goal.id) || [])
      .filter((child) => {
        if (hideArchived && archivedIds.has(child.id)) {
          return false;
        }
        return !hideCompleted || !child.completedAt;
      })
      .map(buildNode);

    return { goal, children };
  };

  const rootGoals = goals.filter((goal) => {
    if (hideArchived && archivedIds.has(goal.id)) {
      return false;
    }
    if (hideCompleted && goal.completedAt) {
      return false;
    }

    if (!goal.parentIds?.length) {
      return true;
    }

    return !goal.parentIds.some((parentId) => goalMap.has(parentId));
  });

  return rootGoals.map(buildNode);
};
